import { toast } from '../utils/toast.js';

class CartState {
  constructor() {
    this.cart = null;
    this.listeners = new Set();
    this.isUpdating = false;
    this.requestTimeout = 8000; 
  }

  
  async fetchWithTimeout(url, options = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  
  async handleErrorResponse(response, defaultMessage) {
    if (response.status === 429) {
      toast.error('Too many requests. Please wait a moment and try again.');
    } else if (response.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else {
      const errorData = await response.json().catch(() => ({}));
      toast.error(errorData.description || defaultMessage);
    }
  }

  
  async init() {
    await this.fetch();
  }

  
  async fetch() {
    try {
      const response = await this.fetchWithTimeout('/cart.js');
      if (response.ok) {
        this.cart = await response.json();
        this.notify();
        return this.cart;
      }
      return null;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('Cart fetch timed out');
      }
      return null;
    }
  }

  
  get() {
    return this.cart;
  }

  
  getItemCount() {
    return this.cart?.item_count || 0;
  }

  
  getTotalPrice() {
    return this.cart?.total_price || 0;
  }

  
  async updateLine(line, quantity) {
    this.isUpdating = true;
    this.notify();

    const defaultError = window.cartStrings?.error || 'Could not update cart';

    try {
      const response = await this.fetchWithTimeout('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });

      if (response.ok) {
        this.cart = await response.json();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: this.cart } }));
      } else {
        await this.handleErrorResponse(response, defaultError);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error('Request timed out. Please check your connection.');
      } else {
        toast.error(defaultError + '. Please try again.');
      }
    } finally {
      this.isUpdating = false;
      this.notify();
    }

    return this.cart;
  }

  
  async addItem(variantId, quantity = 1, properties = {}) {
    this.isUpdating = true;
    this.notify();

    const defaultError = window.cartStrings?.error || 'Could not add to cart';

    try {
      const response = await this.fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity,
          properties,
        }),
      });

      if (response.ok) {
        
        await this.fetch();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: this.cart } }));
      } else {
        await this.handleErrorResponse(response, defaultError);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error('Request timed out. Please check your connection.');
      } else {
        toast.error(defaultError + '. Please try again.');
      }
    } finally {
      this.isUpdating = false;
      this.notify();
    }

    return this.cart;
  }

  
  async updateNote(note) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });

      if (this.cart) {
        this.cart.note = note;
      }
    } catch {
      
    }
  }

  
  subscribe(callback) {
    this.listeners.add(callback);
    
    return () => this.listeners.delete(callback);
  }

  
  notify() {
    this.listeners.forEach((callback) => {
      try {
        callback(this.cart, this.isUpdating);
      } catch {
        
      }
    });
  }

  
  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    cents = parseInt(cents, 10) || 0;

    const moneyFormat = window.themeSettings?.moneyFormat || '${{amount}}';

    const value = cents / 100;
    const amount = value.toFixed(2);
    const amountNoDecimals = Math.floor(value);
    const amountWithCommaSeparator = amount.replace('.', ',');
    const amountNoDecimalsWithCommaSeparator = amountNoDecimals.toString();
    const amountWithApostropheSeparator = amount.replace('.', "'");

    const addThousandSeparator = (numStr, sep) => {
      const parts = numStr.split(/[.,]/);
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      return parts.join(numStr.includes(',') ? ',' : '.');
    };

    const amountWithSpaceSeparator = addThousandSeparator(amount, ' ');

    return moneyFormat
      .replace('{{amount_with_comma_separator}}', amountWithCommaSeparator)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithCommaSeparator)
      .replace('{{amount_with_apostrophe_separator}}', amountWithApostropheSeparator)
      .replace('{{amount_no_decimals_with_space_separator}}', addThousandSeparator(amountNoDecimals.toString(), ' '))
      .replace('{{amount_with_space_separator}}', amountWithSpaceSeparator)
      .replace('{{amount_no_decimals}}', amountNoDecimals.toString())
      .replace('{{amount}}', amount);
  }

  
  getSizedImageUrl(url, size) {
    if (!url) return '';
    const match = url.match(/^(.+?)(\.(jpg|jpeg|png|gif|webp))(\?.*)?$/i);
    if (match) {
      return `${match[1]}_${size}${match[2]}${match[4] || ''}`;
    }
    return url;
  }
}

// Singleton export
export const cartState = new CartState();
export default cartState;
